import React, { Component } from 'react';
import { reduce, each, find, uniq, values, filter, delay } from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import ReactResizeDetector from 'react-resize-detector';
import { fetchTVFieldMappings, deleteTVFieldMapping } from '../../SetupActions';
import ProgressBar from '../../../../components/progress-bar';
import { getViewableDimensions } from '../../../App/AppReducer';
import GroupRowInnerRenderer from './GroupRowInnerRenderer';
import CRMPathRenderer from './CRMPathRenderer';
import TVPathRenderer from './TVPathRenderer';
import AddModal from './AddModal';
import ConfirmButton from '../../../../components/confirm-button';
import ValueMappingCellRenderer from './ValueMappingCellRenderer';

function ActionRenderer(props) {
  const { isDeleting, requestDelete, value } = props;

  return (
    <ConfirmButton
      size="xs"
      disabled={isDeleting}
      confirmStyle="danger"
      onConfirm={() => { requestDelete(value); }}
    >
      <Fa name={isDeleting ? 'spinner' : 'trash'} spin={isDeleting} /> Delete
    </ConfirmButton>
  )
}
ActionRenderer.propTypes = {
  isDeleting: PropTypes.bool,
  value: PropTypes.string.isRequired,
  requestDelete: PropTypes.func,
};
ActionRenderer.defaultProps = {
  isDeleting: false,
  requestDelete: () => {},
};


class TruVoiceFieldsInnerPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      loaded: false,
      data: [],
      tvFields: [],
      multiClients: false,
      navBarHeight: 0,
      clients: {},
      programs: {},
      showModal: false,
      deleting: {}, // indexed by _id
      hasValueMappings: false,
    };
    this.onGridReady = this.onGridReady.bind(this);
    this.onResizNavBar = this.onResizNavBar.bind(this);
    this.showModal = this.showModal.bind(this);
    this.hideModal = this.hideModal.bind(this);
    this.handleAddMapping = this.handleAddMapping.bind(this);
    this.getActionCellRenderParams = this.getActionCellRenderParams.bind(this);
    this.getValueMappingCellRenderParams = this.getValueMappingCellRenderParams.bind(this);
    this.requestDelete = this.requestDelete.bind(this);
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  onResizNavBar(width, navBarHeight) {
    this.setState({ navBarHeight });
  }

  showModal() {
    this.setState({ showModal: true });
  }

  hideModal() {
    this.setState({ showModal: false });
  }

  checkHasValueMappings(data) {
    // picklist: 20,
    // multipicklist: 21,
    const f = filter(data, (d) => {
      return d.tv_field?.dataType === 20 || d.tv_field?.dataType === 21;
    });
    return f.length > 0;
  }

  handleAddMapping(results) {
    const { data } = this.state;
    results.tv_field = find(this.state.tvFields, { id: results.field_id });
    results.accessible = true;
    const newData = [...data, results];
    const hasValueMappings = this.checkHasValueMappings(newData);
    this.setState({ hasValueMappings, data: newData }, () => {
      delay(() => {
        if (this.gridApi) {
          const node = this.gridApi.getRowNode(results._id);
          this.gridApi.flashCells({ rowNodes: [node] });
        }
      }, 400);
    });
  }

  componentDidMount() {
    this.loadData();
  }

  requestDelete(id) {
    if (this.state.deleting[id]) {
      return;
    }
    const { source } = this.props;
    this.setState({ deleting: { ...this.state.deleting, [id]: true } }, () => {
      // this.gridApi.refreshCells({ rowNodes: [node], force: true });
      // this.gridApi.flashCells({ rowNodes: [node] });
      if (this.gridApi) {
        const node = this.gridApi.getRowNode(id);
        this.gridApi.refreshCells({ rowNodes: [node], force: true });
      }
      this.props.dispatch(deleteTVFieldMapping(source, id))
        .then(
          () => {
            const newData = filter(this.state.data, (d) => {
              return d._id !== id;
            });
            const hasValueMappings = this.checkHasValueMappings(newData);
            if (this.gridApi) {
              const node = this.gridApi.getRowNode(id);
              this.gridApi.flashCells({ rowNodes: [node] });
              delay(() => {
                this.setState({ data: newData, hasValueMappings });
              }, 100);
            } else {
              this.setState({ data: newData, hasValueMappings });
            }
          },
        );
    });
  }

  getActionCellRenderParams(params) {
    if (!params.colDef) {
      return null;
    }
    const { value } = params;
    const { deleting } = this.state;

    const isDeleting = deleting[value] === true;

    return {
      component: ActionRenderer,
      params: {
        isDeleting,
        requestDelete: this.requestDelete,
      },
    };
  }

  getValueMappingCellRenderParams(params) {
    if (!params.colDef) {
      return null;
    }
    const { source } = this.props;
    const { value } = params;
    const { deleting } = this.state;

    const disabled = deleting[value] === true;
    return {
      component: ValueMappingCellRenderer,
      params: {
        disabled,
        source,
      },
    };
  }

  prepareData(data) {
    const tvFields = [];
    const clients = {};
    const programs = {};

    const ret = reduce(data, (items, datum) => {
      clients[datum.client_id] = datum.client_name;
      programs[datum.program_id] = {
        name: datum.name,
        type: datum.type_name,
        type_value: datum.type_value,
      };
      each(datum.tv_fields, (tvField) => {
        tvFields.push({
          ...tvField,
          client_id: datum.client_id,
          program_id: datum.program_id,
          client_name: datum.client_name,
          program_name: datum.name,
          type_name: datum.type_name,
          type_value: datum.type_value,
        });
      });
      each(datum.ts_fields, (tsField) => {
        items.push({
          ...tsField,
        });
      });
      return items;
    }, []);
    const hasValueMappings = this.checkHasValueMappings(ret);

    this.setState({
      data: ret,
      hasValueMappings,
      tvFields,
      multiClients: uniq(values(clients)).length > 1,
      clients,
      programs,
    });
  }


  loadData() {
    this.setState({
      loading: true,
      loaded: false,
      data: [],
      tvFields: [],
      multiClients: false,
    }, () => {
      const { source } = this.props;
      this.props.dispatch(fetchTVFieldMappings(source))
        .then(
          (data) => {
            this.prepareData(data);
            this.setState({
              loading: false,
              loaded: true,
            });
          },
          () => {
            this.setState({
              loading: false,
              loaded: false,
              data: [],
            });
          },
        );
    });
    
  }

  renderTable() {
    const { viewableDimensions } = this.props;
    const {
      data, navBarHeight, clients, programs,
      multiClients, hasValueMappings,
    } = this.state;

    const viewableHeight = viewableDimensions.height - navBarHeight;

    const columnDefs = [
      ...(multiClients ? [
        {
          headerName: 'Client Id',
          field: 'client_id',
          suppressMenu: true,
          rowGroup: true,
          rowGroupIndex: 0,
          hide: true,
          cellRendererParams: {
            isClient: true,
          },
        },
      ] : []),
      {
        headerName: 'Program Id',
        field: 'program_id',
        suppressMenu: true,
        rowGroup: true,
        rowGroupIndex: multiClients ? 1 : 0,
        hide: true,
        cellRendererParams: {
          isProgram: true,
        },
      },
      {
        headerName: 'CRM Field',
        field: 'search_paths',
        suppressMenu: true,
        width: 400,
        cellRenderer: CRMPathRenderer,
        cellRendererParams: {
          paddingLeft: 60,
        },
        pinned: 'left',
      },

      {
        headerName: 'TruVoice Field',
        field: 'tv_field.id',
        suppressMenu: true,
        cellRenderer: TVPathRenderer,
        width: 450,
        cellRendererParams: {
        },
      },
      ...(hasValueMappings ? [{
        headerName: 'Value Mappings',
        field: '_id',
        suppressMenu: true,
        width: 160,
        pinned: 'right',
        cellRendererSelector: this.getValueMappingCellRenderParams,
      }] : []),
      {
        headerName: 'Action',
        field: '_id',
        suppressMenu: true,
        width: 100,
        pinned: 'right',
        cellRendererSelector: this.getActionCellRenderParams,
      },
    ];
    if (data.length === 0) {
      return (
        <Button onClick={this.showModal} bsStyle="primary" disabled={this.state.showModal}>
          <Fa name="plus" /> Add New Mapping
        </Button>
      );
    }

    return (
      <div>
        <nav className="navbar navbar-default navbar-controls">
          <div className="navbar-form navbar-right" style={{ marginRight: 5 }}>
            <Button onClick={this.showModal} bsStyle="primary" disabled={this.state.showModal}>
              <Fa name="plus" /> Add New Mapping
            </Button>
          </div>
          <ReactResizeDetector handleHeight onResize={this.onResizNavBar} />
        </nav>
        <div className={`ag-theme-alpine`} style={{ height: viewableHeight, width: '100%' }}>
          <AgGridReact
            reactNext
            enableColResize
            enableSorting
            animateRows
            defaultColDef={{
              resizable: true,
            }}
            groupUseEntireRow
            groupDisplayType="groupRows"
            groupDefaultExpanded={-1}
            groupRowRendererParams={{
              innerRenderer: GroupRowInnerRenderer,
            }}
            columnDefs={columnDefs}
            getRowId={(params) => params.data?._id}
            onGridReady={this.onGridReady}
            rowData={data}
            context={{
              clients,
              programs,
            }}
          />
        </div>
      </div>
    );
  }

  renderModal() {
    const {
      showModal, tvFields, clients, programs,
      multiClients,
    } = this.state;
    const { source } = this.props;
    return (
      <AddModal
        show={showModal}
        onHide={this.hideModal}
        tvFields={tvFields}
        clients={clients}
        programs={programs}
        multiClients={multiClients}
        source={source}
        onAdded={this.handleAddMapping}
      />
    );
  }

  render() {
    const { loading, loaded } = this.state;
    const { source } = this.props;
    return (
      <div>
        <Helmet title={`TruVoice ${source} Field Mappings`} />
        {loading  && <ProgressBar />}
        {!loading && loaded && this.renderTable()}
        {!loading && loaded && this.renderModal()}
        <div style={{ height: '100px' }}></div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    viewableDimensions: getViewableDimensions(state),
  };
}

TruVoiceFieldsInnerPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,
  viewableDimensions: PropTypes.shape({
    height: PropTypes.number,
  }).isRequired,
  source: PropTypes.string.isRequired,
};

TruVoiceFieldsInnerPage.contextTypes = {
  router: PropTypes.shape({}),
};

export default withRouter(connect(mapStateToProps)(TruVoiceFieldsInnerPage));
